import { Component, inject, OnInit, ViewEncapsulation } from '@angular/core'
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { LoggerService } from '@danangelus/ngx-app-core'
import { ConfirmationService } from 'primeng/api'
import { CheckboxModule } from 'primeng/checkbox'
import { ConfirmDialogModule } from 'primeng/confirmdialog'
import { FloatLabelModule } from 'primeng/floatlabel'
import { InputTextModule } from 'primeng/inputtext'
import { KeyFilterModule } from 'primeng/keyfilter'
import { MessageModule } from 'primeng/message'
import { SelectButtonModule } from 'primeng/selectbutton'
import { ToggleSwitchModule } from 'primeng/toggleswitch'
import { LdapManagerService } from '../../../../core/services/ldap-manager.service'
import { ValidationUtils } from '../../../../core/utils/validation.utils'
import { GatewayError } from '../../../../models/gateway.model'
import { Group } from '../../../../models/group.model'

const log = LoggerService.get('EditGroupComponent')

@Component({
    selector: 'edit-group',
    standalone: true,
    imports: [
        // PrimeNG
        CheckboxModule,
        ConfirmDialogModule,
        FloatLabelModule,
        InputTextModule,
        KeyFilterModule,
        MessageModule,
        SelectButtonModule,
        ToggleSwitchModule,
        // Angular
        ReactiveFormsModule,
    ],
    providers: [ConfirmationService],
    templateUrl: './edit-group.component.html',
    styleUrl: './edit-group.component.sass',
    encapsulation: ViewEncapsulation.None
})
export class EditGroupComponent implements OnInit {

    router = inject(Router)
    route = inject(ActivatedRoute)
    confirmationService = inject(ConfirmationService)
    ldapManagerService = inject(LdapManagerService)

    // @ts-expect-error always created
    form: UntypedFormGroup

    messageType = ''
    editingGroup: boolean | undefined

    id = ''
    group: Group | undefined

    async ngOnInit() {
        this.route.params.subscribe(params => {
            this.id = params['groupId']
        })
        if (this.id) {
            this.group = await this.loadGroup(this.id)
        } else {
            this.group = new Group({ locked: false })
        }

        this.form = new UntypedFormGroup({
            groupId: new UntypedFormControl(this.group?.groupId, Validators.required),
            displayName: new UntypedFormControl(this.group?.displayName || this.group?.groupId, Validators.required),
            description: new UntypedFormControl(this.group?.description, []),
            locked: new UntypedFormControl(this.group?.locked, Validators.required)
        })

        if (this.id) {
            this.form.get('groupId')?.disable()
        }
    }

    async cancel(): Promise<void> {
        await this.router.navigateByUrl('/administration/group', { replaceUrl: true })
    }

    async loadGroup(id: string): Promise<Group | undefined> {
        return this.ldapManagerService.loadGroup(id)
    }

    async save(): Promise<void> {
        await this.validateForm()
        if (this.form.invalid) {
            this.messageType = 'FORM_INVALID'
            return
        }
        this.group = new Group(this.form.value)
        if (this.id) {
            // Only apply the group ID if the group is new
            this.group!.groupId = undefined
        }

        let editResponse: any
        if (!this.id) {
            editResponse = await this.ldapManagerService.createGroup(this.group!)
        } else {
            editResponse = await this.ldapManagerService.saveGroup(this.id, this.group!)
        }

        if (editResponse instanceof GatewayError) {
            switch (editResponse.error) {
                case 'NOT_EXISTS':
                case 'NOT_UNIQUE':
                    this.messageType = editResponse.error
                    break
                default:
                    this.messageType = 'ERROR_SAVING'
            }
        } else {
            await this.cancel()
        }
    }

    async validateForm(): Promise<void> {
        Object.values(this.form.controls).forEach(control => {
            control.markAsDirty()
            control.updateValueAndValidity()
        })
    }

    async deleteGroup(): Promise<void> {
        // ToDo :: Internationalisation
        this.confirmationService.confirm({
            message: 'Are you sure you want to delete this group?',
            header: 'Delete Group',
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                try {
                    const response = await this.ldapManagerService.deleteGroup(this.group!.groupId!)
                    if (response instanceof GatewayError) {
                        switch (response.error) {
                            case 'NOT_EXISTS':
                            case 'IN_USE':
                                this.messageType = response.error
                                break
                            default:
                                this.messageType = 'ERROR_SAVING'
                        }
                    } else {
                        await this.cancel()
                    }
                } catch (e) {
                    log.error(`Error deleting group: ${e}`)
                    this.messageType = 'ERROR_SAVING'
                }
            },
            reject: () => {
                this.messageType = 'DELETION_CANCELLED'
            }
        })
        log.info('deleteGroup', `You are trying to delete group with CN ID: ${this.group?.groupId}`)
    }

    protected readonly ValidationUtils = ValidationUtils
}
